import {ChangeDetectorRef, Component, Inject, OnDestroy, OnInit} from '@angular/core';
import {
  BatchExportSchedule,
  BenchmarkCombo,
  getBenchmarkComboDefault,
  MpeDialogPage,
  SchedulePane
} from './mpe-dialog-helper';
import {MatDialog, MatDialogRef} from '@angular/material/dialog';
import {
  BatchExportMetricSection,
  closeSelectionHierarchy,
  getOriginalBatchExportMetricSections,
  getOverviewMetricSection,
  HasMetricAndBenchmarkCombo,
  overViewId,
  setSelectedScheduleEmPageOptionValues,
  setSelectedScheduleEmTrendOptionValues,
  setSelectedScheduleNpvOptionValues,
  setSelectedScheduleOverviewOptionValues
} from '../batch-export-helper';
import {FilterCriteria, IAppState, INITIAL_STATE} from '../../store/IAppState';
import {NgRedux, select} from '@angular-redux/store';
import {isFeatureEnabled} from '../../shared/helpers';
import {FeatureToggleEntries} from '../../shared/feature-toggle-settings-enum';
import {combineLatest, Observable, Subscription} from 'rxjs';
import {FeatureToggleSetting, UserProfile} from '../../shared/models';
import * as _ from 'lodash';
import {distinctUntilChanged} from 'rxjs/operators';
import {BatchExportServiceToken, HttpBatchExportService} from '../services/batch-export.service';
import {MetricType} from '../../shared/metric-types';
import {
  BatchPdfOption,
  EMPageOption,
  EMTrendOption,
  NPVPageOption,
  OverviewPageOption
} from '../../shared/export/export';
import {FrontEndTelehealthFlag} from '../../shared/enums';

@Component({
  selector: 'app-multi-provider-export-redesign',
  templateUrl: './multi-provider-export-redesign.component.html',
  styleUrls: ['./multi-provider-export-redesign.component.scss']
})
export class MultiProviderExportRedesignComponent implements OnInit, OnDestroy {

  private static wrvuBenchmarkCombo: BenchmarkCombo;
  private static npvBenchmarkCombo: BenchmarkCombo;
  private static emBenchmarkCombo: BenchmarkCombo;

  @select(['data', 'featureToggleSettings'])
  private readonly featureToggleSettings$: Observable<FeatureToggleSetting[]>;
  @select(['data', 'userProfile'])
  private readonly userProfile$: Observable<UserProfile>;
  @select(['display', 'selectedProviders'])
  private readonly selectedProviders$: Observable<number>;
  @select(['filters'])
  private readonly filters$: Observable<FilterCriteria>;
  private filters: FilterCriteria;
  currentPage: MpeDialogPage;
  MpeDialogPage = MpeDialogPage;
  nextButtonText: string;
  backButtonText: string;
  nextButtonValue: MpeDialogPage;
  backButtonValue: MpeDialogPage;
  schedulePaneInfo: SchedulePane = {
    reportName: '',
    isMonthly: false
  };
  originalBatchExportMetricSections: BatchExportMetricSection[] = [];
  currentBatchExportMetricSections: BatchExportMetricSection[] = [];
  readonly overViewId = overViewId;
  overviewMetricSection: BatchExportMetricSection;
  numberOfProviders = 0;
  somethingSelected = false;
  permittedToGoToSummary = true;
  showErrorMessage = false;
  // Feature toggles
  canShowEmTrend = false;
  canShowNpv = false;
  canShowWrvuTrend = false;
  canShowScheduler = false;
  canShowBenchmarkSelector = false;
  canShowNpvLocation = false;
  private featureToggleSubscription: Subscription;

  private static linkBenchmarkCombo(item: HasMetricAndBenchmarkCombo): void {
    switch (item.metric) {
      case MetricType.OverviewWrvu:
        item.benchmarkCombo = MultiProviderExportRedesignComponent.wrvuBenchmarkCombo;
        break;
      case MetricType.OverviewNpv:
        item.benchmarkCombo = MultiProviderExportRedesignComponent.npvBenchmarkCombo;
        break;
      case MetricType.OverviewEandM:
        item.benchmarkCombo = MultiProviderExportRedesignComponent.emBenchmarkCombo;
    }
  }

  private static setLinkedBenchmarkCombos(originalBatchExportMetricSections: BatchExportMetricSection[],
                                          overviewOptions: BatchPdfOption[]): void {
    MultiProviderExportRedesignComponent.setLinkedBenchmarkCombosForMetricSections(originalBatchExportMetricSections);
    MultiProviderExportRedesignComponent.setLinkedBenchmarkCombosForPdfOptions(overviewOptions);
  }

  private static setLinkedBenchmarkCombosForMetricSections(batchExportMetricSections: BatchExportMetricSection[]): void {
    batchExportMetricSections.forEach(s => {
      MultiProviderExportRedesignComponent.linkBenchmarkCombo(s);
    });
  }

  private static setLinkedBenchmarkCombosForPdfOptions(options: BatchPdfOption[]): void {
    options.forEach(o => {
      MultiProviderExportRedesignComponent.linkBenchmarkCombo(o);
      MultiProviderExportRedesignComponent.setLinkedBenchmarkCombosForPdfOptions(o.children);
    });
  }

  private static setStaticBenchmarkCombosBasesOnTelehealthFlag(telehealthFlag: FrontEndTelehealthFlag): void {
    MultiProviderExportRedesignComponent.wrvuBenchmarkCombo = getBenchmarkComboDefault(false,
      telehealthFlag);
    MultiProviderExportRedesignComponent.npvBenchmarkCombo = getBenchmarkComboDefault(true,
      telehealthFlag);
    MultiProviderExportRedesignComponent.emBenchmarkCombo = getBenchmarkComboDefault(true,
      telehealthFlag);
  }

  constructor(public dialogRef: MatDialogRef<MultiProviderExportRedesignComponent>,
              public dialog: MatDialog,
              private cdr: ChangeDetectorRef,
              private ngRedux: NgRedux<IAppState>,
              @Inject(BatchExportServiceToken) private batchExportService: HttpBatchExportService) {
  }

  ngOnInit() {
    this.updatePage(MpeDialogPage.SETUP);
    this.featureToggleSubscription = combineLatest([
      this.featureToggleSettings$.pipe(distinctUntilChanged((type1, type2) =>
        _.isEqual(type1, type2))),
      this.userProfile$.pipe(distinctUntilChanged((type1, type2) =>
        _.isEqual(type1, type2))),
      this.selectedProviders$.pipe(distinctUntilChanged((type1, type2) =>
        _.isEqual(type1, type2))),
      this.filters$.pipe(distinctUntilChanged((type1, type2) =>
        _.isEqual(type1, type2)))
    ]).subscribe(([featureSettings, userProfile, selectedProviders, filters]: [FeatureToggleSetting[],
      UserProfile, number, FilterCriteria]) => {
      if (!_.isEqual(featureSettings, INITIAL_STATE.data.featureToggleSettings)) {
        this.canShowScheduler = isFeatureEnabled(FeatureToggleEntries.MULTI_PROVIDER_EXPORT_SCHEDULING, featureSettings, userProfile);
        this.canShowEmTrend = isFeatureEnabled(FeatureToggleEntries.BATCH_EXPORT_EM_TREND, featureSettings, userProfile);
        this.canShowNpv = isFeatureEnabled(FeatureToggleEntries.NPV_MULTI_PROVIDER_EXPORT, featureSettings, userProfile);
        this.canShowNpvLocation = isFeatureEnabled(FeatureToggleEntries.MPE_NPV_BY_LOCATION, featureSettings, userProfile);
        this.canShowWrvuTrend = isFeatureEnabled(FeatureToggleEntries.BATCH_EXPORT_WRVU_TREND, featureSettings, userProfile);
        this.canShowBenchmarkSelector = isFeatureEnabled(FeatureToggleEntries.MULTI_PROVIDER_BENCHMARK_SELECTORS,
          featureSettings, userProfile);
        this.originalBatchExportMetricSections = getOriginalBatchExportMetricSections(this.canShowNpvLocation,
          this.canShowWrvuTrend, filters.telehealthFlag ?? FrontEndTelehealthFlag.ALL_VISIT_TYPES);
        this.overviewMetricSection = getOverviewMetricSection(filters.telehealthFlag ?? FrontEndTelehealthFlag.ALL_VISIT_TYPES);
        MultiProviderExportRedesignComponent.setStaticBenchmarkCombosBasesOnTelehealthFlag(filters.telehealthFlag ??
          FrontEndTelehealthFlag.ALL_VISIT_TYPES);
        MultiProviderExportRedesignComponent.setLinkedBenchmarkCombos(this.originalBatchExportMetricSections,
          this.overviewMetricSection.options);
        this.currentBatchExportMetricSections = this.originalBatchExportMetricSections.slice();
        this.numberOfProviders = selectedProviders;
        this.filters = filters;
      }
    });
  }

  ngOnDestroy() {
    this.featureToggleSubscription?.unsubscribe();
    this.clearValues();
  }

  private clearValues(): void {
    this.currentBatchExportMetricSections.concat([this.overviewMetricSection]).forEach(b => {
      closeSelectionHierarchy(b.options);
    });
  }

  onSchedulePaneInfoChange(): void {
    this.permittedToGoToSummary = this.isPermittedToGoToSummary();
  }

  updatePage(page?: MpeDialogPage): void {
    this.cdr.detectChanges();
    if (page) {
      this.permittedToGoToSummary = this.isPermittedToGoToSummary();
      this.currentPage = page;
      switch (this.currentPage) {
        case MpeDialogPage.SETUP:
          this.setUpNextButton('Next', MpeDialogPage.SUMMARY);
          this.setUpBackButton('Cancel', MpeDialogPage.CLOSED);
          break;
        case MpeDialogPage.SUMMARY:
          this.setUpNextButton('Export', MpeDialogPage.EXPORTED);
          this.setUpBackButton('Edit', MpeDialogPage.SETUP);
          break;
        case MpeDialogPage.EXPORTED:
          this.export();
          this.setUpBackButton('', MpeDialogPage.NONE);
          this.setUpNextButton('Done', MpeDialogPage.CLOSED);
          break;
        case MpeDialogPage.CLOSED:
          this.dialogRef.close();
      }
    }
  }

  private setUpNextButton(text: string, value: MpeDialogPage) {
    this.nextButtonText = text;
    this.nextButtonValue = value;
  }

  private setUpBackButton(text: string, value: MpeDialogPage) {
    this.backButtonText = text;
    this.backButtonValue = value;
  }

  isPermittedToGoToSummary(): boolean {
    return this.somethingSelected && this.schedulePaneInfo.reportName.length > 0;
  }

  onItemSelected(somethingSelected: boolean): void {
    this.somethingSelected = somethingSelected;
    this.permittedToGoToSummary = this.isPermittedToGoToSummary();
  }

  onBatchExportOptionsSubmitted(batchExportMetricSections: BatchExportMetricSection[]): void {
    this.currentBatchExportMetricSections = batchExportMetricSections;
  }

  private export() {
    const selections = this.getBatchExportScheduleInfoFromUserInput(this.currentBatchExportMetricSections, this.filters);
    this.batchExportService.saveBatchExportScheduleInformation(selections, this.filters.memberKey).subscribe();
  }

  private getBatchExportScheduleInfoFromUserInput(batchExportMetricSections: BatchExportMetricSection[],
                                                  filters: FilterCriteria): BatchExportSchedule {
    const initialSchedule: BatchExportSchedule = {
      reportName: this.schedulePaneInfo.reportName,
      nodePath: filters.nodePath,
      dateRangeOption: filters.dateRange.selectedDateRangeOption ?? 0,
      scheduleType: this.schedulePaneInfo.isMonthly ? 1 : 0,
      startDateInMonths: (filters.dateRange.startYear * 12) + filters.dateRange.startMonth,
      endDateInMonths: (filters.dateRange.endYear * 12) + filters.dateRange.endMonth,
      isMarkedForDelete: false,
      payerCategoryKey: filters.payerCategory.payerCategoryKey,
      payerKey: filters.payerKey,
      payerType: filters.payerType,
      lagKey: filters.lagKey,
      invoiceStatus: filters.invoiceStatus,
      isOutpatientRequired: false,
      isInPatientRequired: false,
      isEmergencyMedicineRequired: false,
      isOpthalmologyRequired: false,
      billingAreaKey: filters.memberBillingArea.memberBillingAreaKey,
      telehealthFlag: filters.telehealthFlag ?? 0,
      isOverviewRequired: false,
      isEmPageRequired: false,
      isEmPageOutpatientRequired: false,
      isEmPageInPatientRequired: false,
      isEmPageEmergencyMedicineRequired: false,
      isEmPageOpthalmologyRequired: false,
      isNewPatientVisitsTrendPageRequired: false,
      memberLocationKeys: filters.memberLocationKeys,
      isEmTrendPageRequired: false,
      emTrendRangeNewPatientVisit: false,
      emTrendRangeEstablishedPatientVisit: false,
      emTrendRangeConsultation: false,
      emTrendRangeInitialHospital: false,
      emTrendRangeSubsequentHospital: false,
      emTrendRangeHospitalDischarge: false,
      emTrendRangeEmergencyMedicine: false,
      emTrendRangeNpvEyeExam: false,
      emTrendRangeEstablishedEyeExam: false
    };
    const emPageSection = batchExportMetricSections.find(s => s.metric === MetricType.EandM);
    const emTrendSection = batchExportMetricSections.find(s => s.metric === MetricType.EandMTrend);
    const npvSection = batchExportMetricSections.find(s => s.metric === MetricType.NewPatientVisits);
    setSelectedScheduleOverviewOptionValues(initialSchedule, this.overviewMetricSection.options.map(o => <OverviewPageOption>o));
    if (emPageSection) {
      setSelectedScheduleEmPageOptionValues(initialSchedule, emPageSection.options.map(o => <EMPageOption>o));
    }
    if (emTrendSection) {
      setSelectedScheduleEmTrendOptionValues(initialSchedule, emTrendSection.options.map(o => <EMTrendOption>o));
    }
    if (npvSection) {
      setSelectedScheduleNpvOptionValues(initialSchedule, npvSection.options.map(o => <NPVPageOption>o));
    }
    return initialSchedule;
  }
}
